/*
 * WELLF — PDP designed sensors (Batch 1).  ADDITIVE, isolated, consent-gated.
 * ---------------------------------------------------------------------------
 * Independent of wellf-events.js: loaded on the PRODUCT template only, and ONLY
 * when settings.enable_wellf_sensors is on (pure kill-switch, default OFF). The
 * sensor UI (current-solution picker, "Still thinking?" outlet) is server-rendered
 * in Liquid and only present when the toggle is on, so OFF = zero UI + zero JS.
 *
 * Fires custom events to BOTH sinks after MARKETING + ANALYTICS consent (same rule
 * and mechanism as wellf-events.js — window.fbq/gtag appear post-consent):
 *   S1  CurrentSolution   / wellf_current_solution  {choice}   (repeatable, debounced 1s)
 *   S2  HesitationDeclared/ wellf_hesitation_declared           DELETED 2026-09-04.
 *       The "Still thinking?" outlet it bound to was removed from the buy card on
 *       31 Aug, so [data-wellf-hesitation] matched nothing and the event could never
 *       fire again. Last row 31 Aug. The reveal logic went with it.
 *   NOTE  S1's two events below are LIVE CODE but currently unfirable: the picker is
 *       hidden behind show_solution_picker, off since 2026-09-04. Both come back when
 *       that setting is turned on, which is why they are kept.
 *       The "Still thinking?" outlet was removed from the buy box: it offered nothing
 *       the guarantee strip above it does not already say, and it was never clicked.
 *       The S2 block below is guarded on the element existing, so it simply never runs
 *       and this event now fires zero times. Historical rows stay readable.
 *       GuaranteeOpened   / wellf_guarantee_opened              (on any guarantee-panel open)
 *
 * S2 outlet is pre-rendered hidden and un-hidden on the visitor's SECOND arrival at
 * the buy box — mirrors wellf-events.js PriceReturn (deep >=40%, scrolled past the
 * buy box, then back in view) so it works whether or not enable_wellf_events is on.
 * No storage; in-memory fire-once. Defensive throughout.
 */
(function () {
  'use strict';

  var CS = document.currentScript;
  var DEBUG = !!(CS && CS.getAttribute('data-debug') === 'true');
  if (!document.querySelector('[data-wellf-solution-picker], [data-wellf-buybox]')) return;

  var fired = Object.create(null);

  // ── ask Shopify for the real Customer Privacy API. Shopify's privacy banner stopped
  // installing it on 2026-08-24 (it now writes only a method-less {cachedConsent,
  // consentStatus} stub), which silently killed every event in this file and in
  // wellf-events.js. Best-effort and async; consentOK() below is unchanged and still
  // fails closed if it cannot load — never weaken it. ──
  try {
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }]);
    }
  } catch (e) {}

  // ── consent: require BOTH marketing and analytics ──
  function consentOK() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || !cp.marketingAllowed || !cp.analyticsProcessingAllowed) return false;
      return cp.marketingAllowed() === true && cp.analyticsProcessingAllowed() === true;
    } catch (e) { return false; }
  }

  // ── Meta: trackCustom needs our pixel registered on the window-level fbq. It often
  // already is, so init only when it isn't. init() sends no PageView.
  // (wellf-events.js may also init the same id — benign.) ──
  var META_ID = '1564332575347790';
  var metaInited = false;
  function metaReady() {
    if (typeof window.fbq !== 'function') return false;
    if (metaInited) return true;
    try {
      var state = window.fbq.getState && window.fbq.getState();
      var pixels = (state && state.pixels) || [];
      var already = false;
      for (var i = 0; i < pixels.length; i++) { if (pixels[i] && pixels[i].id === META_ID) { already = true; break; } }
      if (!already) window.fbq('init', META_ID);
      metaInited = true;
    } catch (e) { return false; }
    return true;
  }

  function track(metaName, ga4Name, params) {
    if (!consentOK()) return false;
    var p = params ? Object.assign({}, params) : {};
    p.page = 'pdp';
    try { if (metaReady()) window.fbq('trackCustom', metaName, p); } catch (e) {}
    try { if (typeof window.gtag === 'function') window.gtag('event', ga4Name, p); } catch (e) {}
    if (DEBUG) { try { console.log('[wellf-sensors]', metaName, '/', ga4Name, p); } catch (e) {} }
    return true;
  }
  function once(key, metaName, ga4Name, params) {
    if (fired[key]) return;
    if (track(metaName, ga4Name, params)) fired[key] = true;
  }
  function debounced(gapMs) {
    var last = -Infinity;   // the FIRST call always fires (even within gapMs of page load)
    return function (metaName, ga4Name, params) {
      var now = perfNow();
      if (now - last < gapMs) return;
      last = now;
      track(metaName, ga4Name, params);
    };
  }
  function perfNow() { return (window.performance && performance.now) ? performance.now() : Date.now(); }
  function throttle(fn, ms) {
    var t = 0, queued = false;
    return function () {
      var now = perfNow();
      if (now - t >= ms) { t = now; fn(); }
      else if (!queued) { queued = true; setTimeout(function () { queued = false; t = perfNow(); fn(); }, ms - (now - t)); }
    };
  }

  // =========================================================================
  // S1 · current-solution picker  ·  CurrentSolution {choice} (repeatable, debounced 1s)
  // =========================================================================
  var picker = document.querySelector('[data-wellf-solution-picker]');
  if (picker) {
    var chips = Array.prototype.slice.call(picker.querySelectorAll('[data-wellf-solution]'));
    var lines = Array.prototype.slice.call(picker.querySelectorAll('.bx-picker__line'));
    var outEl = picker.querySelector('[data-wellf-solution-out]');
    // Split the survey from the browsing (people cycle every chip to read our rebuttals,
    // which made the raw distribution unreadable). FIRST selection this pageview =
    // CurrentSolution / wellf_current_solution {choice} — the answer. Each SUBSEQUENT
    // selection = CurrentSolutionExplore / wellf_current_solution_explore {choice, sequence}
    // (sequence = 2, 3, 4…) — the browsing. Both events inherit GA4's automatic
    // ga_session_id, so the two streams reconcile per session in BigQuery — no custom
    // identity added. Re-tapping the already-selected chip carries no new signal, so skip it.
    var solutionCount = 0, lastChoice = null;
    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var choice = chip.getAttribute('data-wellf-solution');
        if (choice === lastChoice) return;
        lastChoice = choice;
        chips.forEach(function (c) { c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'); });
        // Reveal the matching pre-rendered line only (no client-side injection).
        lines.forEach(function (l) { l.classList.toggle('is-shown', l.getAttribute('data-for') === choice); });
        // Expand the (collapsed-when-empty) output box — plain class so it's instant and
        // browser-agnostic; the CSS animates the height so the reveal is smooth.
        if (outEl) outEl.classList.add('is-answered');
        solutionCount++;
        if (solutionCount === 1) {
          track('CurrentSolution', 'wellf_current_solution', { choice: choice });
        } else {
          track('CurrentSolutionExplore', 'wellf_current_solution_explore', { choice: choice, sequence: solutionCount });
        }
      }, { passive: true });
    });
  }

  // =========================================================================
  // GuaranteeOpened  ·  on ANY guarantee-panel open (the price-level strip OR the
  // S2 outlet — both are data-wellf-modal-open="guarantee"). Repeatable, debounced.
  // =========================================================================
  var guaranteeFire = debounced(1000);
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-wellf-modal-open="guarantee"]')) {
      guaranteeFire('GuaranteeOpened', 'wellf_guarantee_opened', {});
    }
  }, { passive: true });

  if (DEBUG) { try { console.log('[wellf-sensors] armed (consent gated).'); } catch (e) {} }
})();
